import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles } from '@/mocks/siteData';

type TabKey = string;

const allArticlesTab: TabKey = 'all-articles';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || categories[0]?.slug || '';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [animateKey, setAnimateKey] = useState(0);

  useEffect(() => {
    const tab = searchParams.get('tab') || categories[0]?.slug || '';
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab: TabKey) => {
    if (tab === activeTab) return;
    setSearchParams({ tab });
    setAnimateKey((prev) => prev + 1);
  };

  const filteredArticles = useMemo(() => {
    if (activeTab === allArticlesTab) {
      return articles;
    }
    return articles.filter((a) => a.categoryId === categories.find((c) => c.slug === activeTab)?.id);
  }, [activeTab]);

  const activeCategory = categories.find((c) => c.slug === activeTab);

  const tabs = [
    { key: allArticlesTab, label: '全部文章', icon: 'ri-article-line' },
    ...categories.map((c) => ({ key: c.slug, label: c.name, icon: c.icon })),
  ];

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类浏览' },
  ];

  if (activeCategory) {
    breadcrumbItems.push({ label: activeCategory.name });
  }

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `真人网站分类浏览${activeCategory ? ' - ' + activeCategory.name : ''}`,
        'description': activeCategory ? activeCategory.description : '浏览所有真人网站分类与精选文章',
        'url': `https://recosearch.github.io/category${activeTab ? '?tab=' + activeTab : ''}`,
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Header */}
          <section className="w-full bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                真人网站分类浏览
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                按类别浏览真人网站资源与精选文章，涵盖直播、互动、社交、教育、视频等多个领域的真人网站推荐内容。
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-none flex-wrap">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${tab.icon} text-xs`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Category Intro (if specific category) */}
          {activeCategory && activeTab !== allArticlesTab && (
            <section className="w-full bg-background-100">
              <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="relative w-full md:w-64 h-40 md:h-40 flex-shrink-0 rounded-xl overflow-hidden">
                    <img
                      src={activeCategory.imageUrl}
                      alt={activeCategory.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
                        <i className={`${activeCategory.icon} text-primary-600 text-sm`}></i>
                      </div>
                      <h2 className="font-heading text-xl text-foreground-900">{activeCategory.name}</h2>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{activeCategory.description}</p>
                    <span className="text-xs text-foreground-500">共 {activeCategory.count} 个网站 · {filteredArticles.length} 篇文章</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Articles Grid */}
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10" key={animateKey}>
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {filteredArticles.map((article) => {
                    const cat = categories.find((c) => c.id === article.categoryId);
                    return (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer flex flex-col"
                      >
                        <div className="relative w-full h-48 overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          {article.featured && (
                            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-medium">
                              精选
                            </span>
                          )}
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          {cat && (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium mb-2 w-fit">
                              {cat.name}
                            </span>
                          )}
                          <h3 className="font-heading text-base text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2 flex-1">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-3 text-[11px] text-foreground-500 pt-3 border-t border-background-200/70">
                            <span>{article.date}</span>
                            <span>·</span>
                            <span>{article.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无文章，请浏览其他分类</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}